import { useRef } from "react";
import { createPortal } from "react-dom";

import { useSpawnHearts } from "../hooks/useHearts";
import { useGameRuntime } from "../context/GameContext";
import { HeartParticleData } from "@/shared/types/data";
import { HeartIcon } from "../icons/heart";

interface LikeButtonProps {
  onClick: () => void;
  onFirstLike?: () => void;
}

export const HeartParticle = ({
  kind,
  label,
  x,
  y,
  life,
  maxLife,
  scale,
}: HeartParticleData) => {
  const progress = life / maxLife;
  const opacity = 1 - progress;

  return (
    <div
      className="absolute pointer-events-none select-none will-change-transform"
      style={{
        left: x,
        top: y,
        opacity,
        transform: `translate(-50%, -50%) scale(${scale})`,
      }}
    >
      {kind === "label" ? (
        <div className="rounded-full border border-red-200/25 bg-red-500/18 px-2 py-1 text-xs font-bold text-white shadow-[0_6px_20px_rgba(244,63,94,0.25)] backdrop-blur-sm">
          {label}
        </div>
      ) : (
        <HeartIcon size={28} filled className="text-red-500" />
      )}
    </div>
  );
};

const LikeButton = ({ onClick, onFirstLike }: LikeButtonProps) => {
  const runtime = useGameRuntime();
  const ref = useRef<HTMLButtonElement | null>(null);
  const particles = useSpawnHearts(runtime, ref);
  const particleLayer = typeof document !== "undefined" ? document.body : null;

  return (
    <>
      <button
        type="button"
        ref={ref}
        className="relative flex items-center justify-center cursor-pointer"
        onClick={() => {
          onFirstLike?.();
          onClick();
        }}
        aria-label="Like stream"
      >
        <HeartIcon size={35} className="text-red-500" />
      </button>

      {particleLayer
        ? createPortal(
            <div className="pointer-events-none fixed inset-0 z-120 overflow-hidden">
              {particles.map((heart) => (
                <HeartParticle key={heart.id} {...heart} />
              ))}
            </div>,
            particleLayer,
          )
        : null}
    </>
  );
};

export default LikeButton;
